import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Face as FaceIcon,
  CheckCircle as CheckCircleIcon,
  CameraAlt as CameraIcon,
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useFaceIO } from '../../context/FaceIOContext'
import { employeeApi } from '../../api/employeeApi'
import { faceApi } from '../../api/faceApi'
import { getTimestampISO } from '../../utils/formatters'

const steps = ['Select Employee', 'Capture Face', 'Verification']

const FaceEnrollment = () => {
  const navigate = useNavigate()
  const { id } = useParams() // Get ID from URL
  const { faceioInstance, error: faceIoError } = useFaceIO()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeStep, setActiveStep] = useState(0)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const [enrollmentStatus, setEnrollmentStatus] = useState('idle') // idle, capturing, processing, completed, error
  const [faceData, setFaceData] = useState(null)
  const [loading, setLoading] = useState(false)

  // Fetch employee if ID is provided
  useEffect(() => {
    const fetchEmployee = async () => {
      if (!id) return

      setLoading(true)
      try {
        const response = await employeeApi.getEmployeeDetails(id)
        if (response.data) {
          const emp = response.data
          // Normalize data
          const normalizedEmp = {
            id: emp.employee_id || emp.id,
            name: `${emp.first_name} ${emp.last_name}`,
            employeeId: emp.employee_code,
            department: emp.department,
            enrolled: emp.face_enrolled,
            email: emp.email
          }
          setSelectedEmployee(normalizedEmp)
          setActiveStep(1)
        }
      } catch (error) {
        console.error("Failed to fetch employee", error)
        toast.error('Failed to load employee details')
        navigate('/org/employees')
      } finally {
        setLoading(false)
      }
    }

    fetchEmployee()
  }, [id, navigate])

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1)
  }

  const handleBack = () => {
    if (id && activeStep === 1) {
      navigate('/org/employees')
    } else {
      setActiveStep((prevStep) => prevStep - 1)
    }
  }

  const startEnrollment = async () => {
    if (!faceioInstance) {
      toast.error('FaceIO not initialized. Check configuration.')
      return
    }

    setEnrollmentStatus('capturing')

    try {
      // 1. Trigger FaceIO Enrollment
      const userInfo = await faceioInstance.enroll({
        locale: "auto",
        confidence: 0.55,
        enrollIntroTimeout: 1,
        idleTimeout: 10,
        replyTimeout: 10,
        userConsent: false,
        payload: {
          id: selectedEmployee.id,
          name: selectedEmployee.name,
          email: selectedEmployee.email,
          employeeId: selectedEmployee.employeeId,
          department: selectedEmployee.department
        }
      })

      console.log('FaceIO Success:', userInfo)

      // 2. Save to Backend
      const enrollmentPayload = {
        employee_id: selectedEmployee.id,
        facial_id: userInfo.facialId,
        timestamp: getTimestampISO(),
        faceio_payload: userInfo.details,
        face_enrolled: true
      }

      const orgId = localStorage.getItem('org_id')
      await faceApi.saveEnrollment(enrollmentPayload, orgId, { skipSuccessToast: true })

      setFaceData(userInfo)
      setEnrollmentStatus('completed')
      toast.success('Face enrolled successfully!')

      // Restart session to allow another enrollment without page refresh
      faceioInstance.restartSession()

      handleNext()

    } catch (errCode) {
      // If object (API error) or code (FaceIO error)
      console.error('Enrollment Failed:', errCode)
      setEnrollmentStatus('error')

      if (errCode.response) {
        // API Error - backend save failed
        toast.error(errCode.response?.data?.detail || 'Failed to save enrollment to server')
        // Restart session even on API error
        faceioInstance.restartSession()
      } else {
        // FaceIO Error
        handleFaceIOError(errCode)
        // Restart session after FaceIO error
        faceioInstance.restartSession()
      }
    }
  }

  const handleFaceIOError = (errCode) => {
    let msg = 'Enrollment failed.'

    switch (errCode) {
      case fioErrCode.PERMISSION_REFUSED:
        msg = 'Camera access denied. Please allow camera permissions and try again.'
        break
      case fioErrCode.NO_FACES_DETECTED:
        msg = 'No face detected. Please position your face clearly in the camera frame.'
        break
      case fioErrCode.UNRECOGNIZED_FACE:
        msg = 'Face not recognized in the system.'
        break
      case fioErrCode.MANY_FACES:
        msg = 'Multiple faces detected. Please ensure only one person is in the frame.'
        break
      case fioErrCode.FACE_DUPLICATION:
        msg = 'This face is already enrolled! Cannot enroll the same person twice.'
        break
      case fioErrCode.MINORS_NOT_ALLOWED:
        msg = 'Minors are not allowed to enroll in this application.'
        break
      case fioErrCode.PAD_ATTACK:
        msg = 'Presentation attack detected. Please use a live face, not a photo or video.'
        break
      case fioErrCode.FACE_MISMATCH:
        msg = 'Facial features do not match. Please try again.'
        break
      case fioErrCode.WRONG_PIN_CODE:
        msg = 'Incorrect PIN code entered.'
        break
      case fioErrCode.PROCESSING_ERR:
        msg = 'Server processing error. Please try again later.'
        break
      case fioErrCode.UNAUTHORIZED:
        msg = 'Application is not authorized. Please contact support.'
        break
      case fioErrCode.TERMS_NOT_ACCEPTED:
        msg = 'Terms and conditions must be accepted to proceed.'
        break
      case fioErrCode.UI_NOT_READY:
        msg = 'FaceIO widget is not ready. Please refresh the page and try again.'
        break
      case fioErrCode.SESSION_EXPIRED:
        msg = 'Session expired. Please start the enrollment process again.'
        break
      case fioErrCode.TIMEOUT:
        msg = 'Operation timed out. Please try again.'
        break
      case fioErrCode.TOO_MANY_REQUESTS:
        msg = 'Too many requests. Please wait a moment and try again.'
        break
      case fioErrCode.EMPTY_ORIGIN:
        msg = 'Invalid request origin. Please contact support.'
        break
      case fioErrCode.FORBIDDEN_ORIGIN:
        msg = 'This domain is not authorized to use FaceIO.'
        break
      case fioErrCode.FORBIDDEN_COUNTRY:
        msg = 'FaceIO is not available in your country.'
        break
      case fioErrCode.SESSION_IN_PROGRESS:
        msg = 'Another enrollment session is already in progress.'
        break
      case fioErrCode.NETWORK_IO:
        msg = 'Network connection error. Please check your internet and try again.'
        break
      default:
        msg = `FaceIO Error: ${errCode}`
        break
    }

    toast.error(msg)
  }

  const handleFinish = () => {
    navigate('/org/employees')
  }

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography>Please select an employee from the list to enroll.</Typography>
            <Button variant="contained" onClick={() => navigate('/org/employees')} sx={{ mt: 2 }}>
              Go to Employee List
            </Button>
          </Box>
        )

      case 1:
        if (loading) return <CircularProgress />
        if (!selectedEmployee) return <Typography color="error">No employee selected</Typography>

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            {enrollmentStatus === 'idle' || enrollmentStatus === 'error' ? (
              <>
                <Avatar sx={{ width: 100, height: 100, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', color: 'text.secondary', mb: 3 }}>
                  <FaceIcon sx={{ fontSize: 60 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom>
                  Ready to Enroll {selectedEmployee?.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ maxWidth: 400, mb: 4 }}>
                  Ensure the employee is facing the camera directly in a well-lit environment. Glasses and masks should be removed.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CameraIcon />}
                  onClick={startEnrollment}
                  sx={{ px: 4, py: 1.5, borderRadius: 2 }}
                >
                  Start Face Capture
                </Button>
                {faceIoError && (
                  <Typography color="error" sx={{ mt: 2 }}>
                    System Error: {faceIoError}
                  </Typography>
                )}
              </>
            ) : (enrollmentStatus === 'capturing' || enrollmentStatus === 'processing') && (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} sx={{ mb: 3 }} />
                <Typography variant="h6">
                  {enrollmentStatus === 'capturing' ? 'Capturing Face Data...' : 'Saving to Server...'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Please follow the on-screen instructions
                </Typography>
              </Box>
            )}
          </Box>
        )

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Avatar sx={{ width: 80, height: 80, bgcolor: '#4caf50', mb: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 50 }} />
            </Avatar>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Enrollment Successful!
            </Typography>
            <Typography variant="body1" color="textSecondary" align="center" sx={{ mb: 4 }}>
              Face data for <strong>{selectedEmployee?.name}</strong> has been securely stored.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, width: '100%', maxWidth: 400, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Enrollment Details:</Typography>
              <Typography variant="body2"><strong>Facial ID:</strong> {faceData?.facialId}</Typography>
              <Typography variant="body2"><strong>Timestamp:</strong> {faceData?.timestamp}</Typography>
            </Paper>

            <Button
              variant="contained"
              onClick={handleFinish}
              sx={{ mt: 4, minWidth: 200 }}
            >
              Back to Employee List
            </Button>
          </Box>
        )

      default:
        return 'Unknown step'
    }
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate(-1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Box>
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                  Face Enrollment
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Register employee biometrics for attendance
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
          <Box sx={{ width: '100%', overflowX: 'auto', mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel={isSmallScreen} sx={{ minWidth: { xs: '100%', md: 'auto' } }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Box sx={{ minHeight: 400 }}>
            {renderStepContent(activeStep)}
          </Box>
        </Paper>
      </Paper>
    </Box>
  )
}

export default FaceEnrollment